<script lang="ts" setup generic="TObj extends BaseObj">
import { ButtonGroup, ToolbarComp, ButtonComp, InputDropdownCheckboxes, DropdownSeparator } from '@/components';
import { ref, computed } from 'vue';
import { watchDebounced } from '@vueuse/core';
import { DropdownItemButton, DropdownItemCustom, DropdownItemMenu, DropdownItemSeparator, type SelectLine } from '@/objects';
import { featureIcons } from '../Menu/featureicons';
import { $ti } from '@/locize';
import type { BaseObj } from '@/objects/BaseObj';
import { IconWithTransform, StackedIcon } from '@/fontawesome';
import type { GridColumnOptions } from '../Grid/Classes';

const props = withDefaults(
    defineProps<{
        refreshList: () => void;
        menuActions: (DropdownItemButton | DropdownItemSeparator | DropdownItemMenu | DropdownItemCustom<any, any>)[];
        quickButtons: (DropdownItemButton | DropdownItemMenu | DropdownItemSeparator | DropdownItemCustom<any, any>)[];
        rightActions?: (DropdownItemButton | DropdownItemMenu | DropdownItemSeparator | DropdownItemCustom<any, any>)[];
        defaultMenuActions: (DropdownItemButton | DropdownItemSeparator)[];
        searchBoxPlaceholder?: string;
        hideDefaultButtons?: boolean;

        filterErrors?: (state: boolean) => void;

        /**
         * callback for when the columns should be resized
         */
        resizeColumns?: () => void;

        /**
         * The text for the action menu this should be a translation key
         * @default ''
         */
        actionMenuText?: string;

        /**
         * If the search field should be hidden
         * @default false
         */
        hideSearchField?: boolean;

        /**
         * If we should show the button for filtering errors
         */
        showErrorFilter?: boolean;

        /**
         * The number of rows with notifications (invalid or warning)
         */
        rowsWithNotifications?: number;

        /**
         * The GUI definition for the object type
         */
        columnDefs?: GridColumnOptions<TObj>[];
    }>(),
    {
        actionMenuText: '',
        hideSearchField: false,
        showErrorFilter: false,
    },
);

const emit = defineEmits<{
    (e: 'clearColumnSettings'): void;
}>();

const searchText = defineModel<string>('searchText');

const filteredColumns = defineModel<Array<string>>('filteredColumns');

const errorFilterState = ref<boolean>(false);

/**
 * We set up an internal debounced searchtext so it is not updated on every keypress
 */
const internalSearchText = ref<string | undefined>(props.hideSearchField ? undefined : (searchText.value ?? ''));
watchDebounced(internalSearchText, (nv) => (searchText.value = nv), { debounce: 250 });

/**
 * Get the actions on the toolbar
 */
const actions = computed((): (DropdownItemMenu | DropdownItemButton | DropdownItemSeparator | DropdownItemCustom<any, any>)[] => {
    if (props.hideDefaultButtons && props.menuActions?.length == 0) {
        return props.quickButtons;
    }

    let buttons = [
        DropdownItemMenu.create({
            translationkey: props.actionMenuText ?? '',
            icon: 'fa-screwdriver-wrench',
            dropdownItems: [],
        }),
        ...props.quickButtons,
    ];

    const defaultActionButton = buttons[0];

    if (!(defaultActionButton instanceof DropdownItemMenu)) {
        throw new Error('The first item in the action list should be a DropdownItemMenu');
    }

    if (!props.hideDefaultButtons) {
        defaultActionButton.dropdownItems.push(...props.defaultMenuActions);
    }

    // Add thre rest of the action items
    defaultActionButton.dropdownItems.push(...props.menuActions);

    // if the first element is the defaultActionButton does not have any items, we remove it
    if (defaultActionButton.dropdownItems.length == 0) {
        buttons = buttons.slice(1);
    }

    return buttons;
});

/**
 * Get the actions on the right side of the toolbar
 */
const rightActions = computed((): (DropdownItemMenu | DropdownItemButton | DropdownItemSeparator | DropdownItemCustom<any, any>)[] => {
    let rightActions = [] as (DropdownItemMenu | DropdownItemButton | DropdownItemSeparator | DropdownItemCustom<any, any>)[];
    if (!props.hideDefaultButtons) {
        rightActions.push(
            DropdownItemButton.create({
                buttonType: 'Secondary',
                icon: 'fa-arrows-rotate',
                action: props.refreshList,
            }),
        );
    }
    return [...(props.rightActions ?? []), ...rightActions];
});

const columnSelectDropdown = ref<InstanceType<typeof InputDropdownCheckboxes>>();

const clearStoredSettings = () => {
    columnSelectDropdown.value?.hide();
    emit('clearColumnSettings');
};

/**
 * We create a GUIDef dropdown selectline based on the columns in the GUI definition
 */
const columnDefsAsSelectline = computed<SelectLine[]>(() => {
    if (!props.columnDefs) {
        return [];
    }

    const checkboxLines =
        props.columnDefs
            .filter((x) => x.colId)
            .map((f) => ({
                value: f.colId ?? null,
                text: f.columnHeaderText ?? '',
            })) ?? [];

    return checkboxLines;
});

const additionaDropdownItems = [
    DropdownItemSeparator.create(),
    DropdownItemButton.create({
        action: clearStoredSettings,
        icon: 'fa-bin-recycle',
        translationkey: 'listcomptoolbar.clearsettings',
    }),
];

/**
 * When the filter button is clicked
 */
const filterClicked = () => {
    errorFilterState.value = !errorFilterState.value;
    if (props.filterErrors) {
        props.filterErrors(errorFilterState.value);
    }
};
</script>
<template>
    <ToolbarComp
        :actions="actions"
        :refresh-action="refreshList"
        v-model:searchtext="internalSearchText"
        :rightActions="rightActions"
        :search-box-placeholder="searchBoxPlaceholder"
    >
        <template #first>
            <ButtonGroup :actions />
            <ButtonComp
                v-if="showErrorFilter === true && filterErrors && (rowsWithNotifications ?? 0) > 0"
                button-type="Custom"
                :icon="featureIcons.WARNING"
                :class="[errorFilterState ? 'btn-warning' : 'btn-outline-warning']"
                :title="$tm('grid.showerrorrows')"
                @click="filterClicked"
                >{{
                    rowsWithNotifications != null ? $ti('listcomptoolbar.rownotificationfilter', { count: rowsWithNotifications }) : ''
                }}</ButtonComp
            >

            <slot name="menu-filters"></slot>
        </template>
        <template #last>
            <InputDropdownCheckboxes
                v-if="filteredColumns !== undefined && columnDefsAsSelectline.length > 0"
                ref="columnSelectDropdown"
                v-model="filteredColumns"
                :data="columnDefsAsSelectline"
                :dropdown-items="additionaDropdownItems"
                :title="$tm('listcomptoolbar.selectcolumns')"
                :show-selected-marker="false"
                :icon="
                    new StackedIcon('fa-columns', [
                        {
                            iconName: 'fas fa-gear',
                            transform: new IconWithTransform({ shrink: 4, right: 8, down: 6, color: '#82795F' }),
                        },
                    ])
                "
            >
            </InputDropdownCheckboxes>
        </template>
    </ToolbarComp>
</template>
